/**
 * PHFHero
 *
 * Full-screen hero section used on PHF landing pages.
 * Displays both the Sponsor a Pet and Purple Heart Foundation logos,
 * a responsive background image with a white overlay, title, subtext,
 * a CTA button linking to an external URL, and an optional disclaimer.
 *
 * Props:
 * - image (object, required): Responsive image URLs with keys:
 *     - src (string): Default fallback image URL
 *     - sm (string): Image URL for 480w
 *     - md (string): Image URL for 1280w
 *     - lg (string): Image URL for 1980w
 * - title (string, required): Main heading text
 * - subtext (string, required): Paragraph below the title
 * - buttonText (string, required): Label for the CTA button
 * - buttonUrl (string, required): External URL the button links to
 * - disclaimer (string, optional): Small print text below the button
 */
const PHFHero = ({ image, title, subtext, buttonText, buttonUrl, disclaimer }) => {
  return (
    <div className="relative min-h-screen flex flex-col pt-[100px]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={image.src}
          srcSet={`${image.sm} 480w, ${image.md} 1280w, ${image.lg} 1980w`}
          sizes="100vw"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-white/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center flex-1 px-4 py-16 text-center">
        {/* Logos */}
        <div className="flex items-center justify-center gap-8 mb-12">
          <img src="https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/sap-logo.svg" alt="Sponsor a Pet" className="h-16 object-contain" />
          <img
            src="https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-logo.svg"
            alt="Purple Heart Foundation"
            className="h-16 object-contain"
          />
        </div>

        {/* Title */}
        <h1 className="!text-[56px] font-bold text-black font-poppins leading-tight max-w-4xl mb-6">
          {title}
        </h1>

        {/* Subtext */}
        <p className="!text-[20px] text-black max-w-2xl mb-10 font-poppins">
          {subtext}
        </p>

        {/* Button */}
        <a
          href={buttonUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-orange hover:bg-orange-dark1 text-white font-semibold !text-[18px] py-4 px-12 rounded-full transition-colors mb-4"
        >
          {buttonText}
        </a>

        {/* Disclaimer */}
        {disclaimer && (
          <p className="text-[14px] text-gray-600 mt-4">{disclaimer}</p>
        )}
      </div>
    </div>
  );
};

export default PHFHero;
